import { FCWithChildren } from '@types';
import React, { useMemo } from 'react';
import moment from 'moment';

import { Button } from '@components';

import { INetworkInfo } from 'sdk-apogee';

import { useAppContext } from '@contexts/AppContext';

import { SEND_INVITE, EXPIRE_NETWORK, LEAVE_NETWORK, ActionTypes } from '../SharedList/SharedList';

type NetworkMembersTableProps = {
  myNetworkStatus: INetworkInfo;
  dispatch: (action: ActionTypes) => void;
};

const NetworkMembersTable: FCWithChildren<NetworkMembersTableProps> = ({
  dispatch,
  myNetworkStatus,
}) => {
  const { user } = useAppContext();

  const { ownerId, ownerEmail, expires, duration } = myNetworkStatus;

  const isHost = user?.id === ownerId;

  // `duration === null` means the host chose "Unlimited", so there is nothing to count down.
  const remainingLabel = useMemo(() => {
    if (duration === null || !expires) return '';

    const minutesLeft = moment.utc(expires).local().diff(moment(), 'minutes');

    if (minutesLeft <= 0) return 'Expired';

    const hours = Math.floor(minutesLeft / 60);
    const minutes = minutesLeft % 60;

    if (!hours) return `${minutes} min remaining`;

    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ${minutes} min remaining`;
  }, [duration, expires]);

  const endSessionHandler = () => dispatch({ type: isHost ? EXPIRE_NETWORK : LEAVE_NETWORK });

  return (
    <section className="w-full">
      <div className="flex flex-wrap items-baseline gap-x-3">
        <h2 className="text-black">Shared Network</h2>
        {remainingLabel && (
          <span className="text-sm text-gray-600 opacity-75">{remainingLabel}</span>
        )}
      </div>

      <p className="mt-1 break-all text-black">
        <span className="uppercase">Host</span> {ownerEmail}
      </p>

      <div className="mt-4 flex flex-wrap gap-4">
        <Button className="flex-1 whitespace-nowrap" layout="outline" onClick={endSessionHandler}>
          {isHost ? 'End Shared Network' : 'Leave Network'}
        </Button>

        {isHost && (
          <Button
            className="flex-1 whitespace-nowrap font-black"
            onClick={() => dispatch({ type: SEND_INVITE })}
          >
            Send Invite
          </Button>
        )}
      </div>
    </section>
  );
};

export default NetworkMembersTable;
